import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Dropdown, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import CreateFlashcardModal from './CreateFlashcardModal';
import GuideListItem from '../GuideListItem';
import { authHeaders, flashcardsFetch } from './flashcardsUtils';
import { guidesFetch } from '../guidesUtils';

function readStoredGuidesView() {
  try {
    const v = localStorage.getItem('guidesView');
    if (v === 'list' || v === 'grid') return v;
  } catch {
    // ignore
  }
  return 'list';
}

function openFlashcardItem(navigate, item) {
  const isDeck = item.type === 'deck' || item.type === 'flashcard_deck';
  if (isDeck) navigate(`/guides/flashcards/decks/${item.id}`);
  else navigate(`/guides/flashcards/sets/${item.id}`);
}

function deletePath(item) {
  const isDeck = item.type === 'deck' || item.type === 'flashcard_deck';
  return isDeck ? `/decks/${item.id}` : `/sets/${item.id}`;
}

export default function FlashcardsListPage() {
  const navigate = useNavigate();
  const outlet = useOutletContext() || {};
  const [items, setItems] = useState([]);
  const [collectionItems, setCollectionItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createType, setCreateType] = useState(null);
  const [localView, setLocalView] = useState(readStoredGuidesView);
  const view = outlet.view ?? localView;
  const setViewMode = outlet.setViewMode || ((v) => {
    const next = v === 'list' ? 'list' : 'grid';
    setLocalView(next);
    try {
      localStorage.setItem('guidesView', next);
    } catch {
      // ignore
    }
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await flashcardsFetch('/mine', { headers: authHeaders() });
      setItems(Array.isArray(data) ? data : []);
      try {
        const collected = await guidesFetch('/collections?types=flashcard_deck,flashcard_set', {
          headers: authHeaders()
        });
        setCollectionItems(Array.isArray(collected) ? collected : []);
      } catch {
        setCollectionItems([]);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load flashcards');
      setItems([]);
      setCollectionItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreated = (created) => {
    if (createType === 'deck') navigate(`/guides/flashcards/decks/${created.id}`);
    else navigate(`/guides/flashcards/sets/${created.id}/edit`);
  };

  const removeItemFromState = (item) => {
    const match = (row) => !(row.type === item.type && row.id === item.id && (row.collectionId || '') === (item.collectionId || ''));
    setItems((prev) => prev.filter(match));
    setCollectionItems((prev) => prev.filter(match));
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await flashcardsFetch(deletePath(item), { method: 'DELETE', headers: authHeaders() });
      removeItemFromState(item);
      toast.success('Deleted');
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  const handleRemoveCollection = async (item) => {
    if (!item.collectionId) return;
    if (!window.confirm(`Remove "${item.title}" from your collection?`)) return;
    try {
      await guidesFetch(`/collections/${item.collectionId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      removeItemFromState(item);
      toast.success('Removed from collection');
    } catch (err) {
      toast.error(err.message || 'Failed to remove from collection');
    }
  };

  const totalCount = items.length + collectionItems.length;
  const countLabel =
    totalCount === 1
      ? '1 item'
      : `${totalCount} decks & sets`;

  const renderCard = (item, keyPrefix = '') => (
    <GuideListItem
      key={`${keyPrefix}${item.type}-${item.id}-${item.collectionId || ''}`}
      item={item}
      view={view}
      onOpen={() => openFlashcardItem(navigate, item)}
      onDelete={item.isOwner && !item.readOnly ? () => handleDelete(item) : null}
      onRemoveCollection={item.isCollection ? () => handleRemoveCollection(item) : null}
    />
  );

  return (
    <div className="guides-list-page">
      <div className="guides-list-header">
        <div>
          <h6 className="mb-0 fw-semibold">Flashcards</h6>
          <p className="guides-page-meta">{countLabel}</p>
        </div>
        <div className="guides-list-actions">
          <div className="radio-inputs" style={{ width: 'fit-content' }}>
            <label className="radio">
              <input
                type="radio"
                name="fc-view-radio"
                checked={view === 'list'}
                onChange={() => setViewMode('list')}
                aria-label="List view"
              />
              <span className="name px-md-3 p-2">
                <i className="bi bi-list me-1" />
                <span className="d-none d-md-inline">List</span>
              </span>
            </label>
            <label className="radio">
              <input
                type="radio"
                name="fc-view-radio"
                checked={view === 'grid'}
                onChange={() => setViewMode('grid')}
                aria-label="Thumbnail view"
              />
              <span className="name px-md-3 p-2">
                <i className="bi bi-grid-3x3-gap me-1" />
                <span className="d-none d-md-inline">Grid</span>
              </span>
            </label>
          </div>
          <Dropdown align="end">
            <Dropdown.Toggle variant="outline-primary" size="sm" className="d-flex align-items-center gap-1">
              <i className="bi bi-plus-lg" />
              <span className="d-none d-md-inline">New</span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setCreateType('deck')}>Deck</Dropdown.Item>
              <Dropdown.Item onClick={() => setCreateType('set')}>Flashcard Set</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {loading ? (
        <div className="guides-loading"><Spinner /></div>
      ) : totalCount === 0 ? (
        <div className="guides-empty card border shadow-none p-4">
          <p className="mb-0 text-muted">
            Create a deck to organize sets, or a standalone flashcard set to get started.
          </p>
        </div>
      ) : (
        <div className="guides-grouped-list">
          {items.length > 0 && (
            <section className="guides-group-section mb-4">
              <h6 className="guides-group-heading smaller text-uppercase text-muted fw-semibold mb-2">
                My content
              </h6>
              <div className={view === 'grid' ? 'guides-grid' : 'guides-list-rows'}>
                {items.map((item) => renderCard(item, 'mine-'))}
              </div>
            </section>
          )}
          {collectionItems.length > 0 && (
            <section className="guides-group-section mb-4">
              <h6 className="guides-group-heading smaller text-uppercase text-muted fw-semibold mb-2">
                Collection
              </h6>
              <div className={view === 'grid' ? 'guides-grid' : 'guides-list-rows'}>
                {collectionItems.map((item) => renderCard(item, 'col-'))}
              </div>
            </section>
          )}
        </div>
      )}

      <CreateFlashcardModal
        show={!!createType}
        type={createType}
        onHide={() => setCreateType(null)}
        onCreated={handleCreated}
      />
    </div>
  );
}
